import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMMITMENTS = ["One-Time", "3 Months", "6 Months", "12 Months"];
const AMOUNTS = ["$250", "$500", "$1,000", "$2,500", "$5,000", "$10,000", "Other"];
const TYPES = ["Monetary", "In-Kind"];

const fieldClass =
  "rounded-none bg-transparent border-0 border-b border-purple-300/30 focus-visible:ring-0 focus-visible:border-purple-300 px-0 py-6 text-white placeholder:text-white/30 text-base";

export default function PartnerForm() {
  const [form, setForm] = useState({
    business: "",
    contact_name: "",
    email: "",
    phone: "",
    recognition_name: "",
    commitment: "One-Time",
    amount: "$250",
    contribution_type: "Monetary",
    in_kind_description: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const chip = (active) =>
    `overline px-4 py-3 border transition-colors ${
      active
        ? "bg-purple-500 border-purple-500 text-white"
        : "border-purple-300/30 text-white/60 hover:border-purple-300/70 hover:text-white"
    }`;

  const submit = async (e) => {
    e.preventDefault();
    if (form.business.length < 2) return toast.error("Please enter your business or organization name.");
    if (form.contact_name.length < 2) return toast.error("Please enter a contact name.");
    if (!form.email.includes("@")) return toast.error("Please enter a valid email.");
    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/partners`, form);
      setSubmitted(true);
      if (res?.data?.emailed) {
        toast.success("Thank you! Your partner inquiry was sent to our team.");
      } else {
        toast.success("Thank you! We received your partner inquiry.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="partner-form-success"
        className="border border-purple-400/40 bg-purple-500/[0.08] p-10 md:p-14 text-center"
      >
        <p className="overline text-purple-200">Hope begins with partnership</p>
        <h3 className="font-serif font-light text-white text-3xl md:text-4xl mt-4 leading-tight">
          Thank you for standing with the <span className="text-purple-300 italic">31 Moms.</span>
        </h3>
        <p className="text-white/70 font-light mt-5 max-w-xl mx-auto">
          Your inquiry reached our team at partners@tandemtfoundation.org. We&rsquo;ll be in touch within a
          couple of business days to finalize your partnership.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="partner-form" className="border border-purple-400/25 bg-purple-500/[0.04] p-7 md:p-12">
      <p className="overline text-purple-200">Become a Community Partner</p>
      <h3 className="font-serif font-light text-white text-3xl md:text-4xl mt-3 leading-tight">
        Stand with us this <span className="text-purple-300 italic">October.</span>
      </h3>

      {/* Commitment */}
      <div className="mt-9">
        <Label className="overline text-white/55">Choose Your Commitment</Label>
        <div className="mt-3 flex flex-wrap gap-2">
          {COMMITMENTS.map((c) => (
            <button
              type="button"
              key={c}
              data-testid={`partner-commitment-${c.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => update("commitment", c)}
              className={chip(form.commitment === c)}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Impact level */}
      <div className="mt-8">
        <Label className="overline text-white/55">Choose Your Impact</Label>
        <div className="mt-3 flex flex-wrap gap-2">
          {AMOUNTS.map((a) => (
            <button
              type="button"
              key={a}
              data-testid={`partner-amount-${a.replace(/[$,]/g, "").toLowerCase()}`}
              onClick={() => update("amount", a)}
              className={chip(form.amount === a)}
            >
              {a}
            </button>
          ))}
        </div>
      </div>

      {/* Contribution type */}
      <div className="mt-8">
        <Label className="overline text-white/55">Contribution Type</Label>
        <div className="mt-3 flex flex-wrap gap-2">
          {TYPES.map((t) => (
            <button
              type="button"
              key={t}
              data-testid={`partner-type-${t.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => update("contribution_type", t)}
              className={chip(form.contribution_type === t)}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {form.contribution_type === "In-Kind" && (
        <div className="mt-8">
          <Label htmlFor="in_kind" className="overline text-white/55">In-Kind Description</Label>
          <Textarea
            id="in_kind"
            data-testid="partner-inkind"
            value={form.in_kind_description}
            onChange={(e) => update("in_kind_description", e.target.value)}
            placeholder="Personal care products, gift cards, comfort items, services…"
            className={`${fieldClass} min-h-[90px] py-3`}
          />
        </div>
      )}

      {/* Details */}
      <div className="mt-9 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
        <div>
          <Label htmlFor="business" className="overline text-white/55">Business / Organization *</Label>
          <Input id="business" data-testid="partner-business" value={form.business} onChange={(e) => update("business", e.target.value)} className={fieldClass} />
        </div>
        <div>
          <Label htmlFor="contact_name" className="overline text-white/55">Contact Name *</Label>
          <Input id="contact_name" data-testid="partner-contact" value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} className={fieldClass} />
        </div>
        <div>
          <Label htmlFor="email" className="overline text-white/55">Email *</Label>
          <Input id="email" type="email" data-testid="partner-email" value={form.email} onChange={(e) => update("email", e.target.value)} className={fieldClass} />
        </div>
        <div>
          <Label htmlFor="phone" className="overline text-white/55">Phone</Label>
          <Input id="phone" data-testid="partner-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={fieldClass} />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="recognition_name" className="overline text-white/55">Recognition Name (how you&rsquo;d like to be listed)</Label>
          <Input id="recognition_name" data-testid="partner-recognition" value={form.recognition_name} onChange={(e) => update("recognition_name", e.target.value)} className={fieldClass} />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="message" className="overline text-white/55">Message (optional)</Label>
          <Textarea id="message" data-testid="partner-message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us how you'd like to help…" className={`${fieldClass} min-h-[90px] py-3`} />
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="partner-submit"
        className="group inline-flex items-center gap-3 mt-9 overline px-8 py-5 bg-purple-500 text-white hover:bg-purple-400 transition-colors disabled:opacity-50"
      >
        {submitting ? "Sending…" : "Become a Partner"} <Send size={15} className="hover-arrow" />
      </button>
      <p className="text-white/45 text-xs font-light mt-5 max-w-xl">
        Tandem T Foundation is a 501(c)(3) nonprofit. EIN 42-3781424. No goods or services are provided in
        exchange for charitable contributions. Prefer to give directly? Zelle, Cash App ($TandemTFoundation),
        or check to Tandem T Foundation.
      </p>
    </form>
  );
}
